import { useAuth } from '@/contexts/AuthContext';
import { useBustlyDesktopOpen } from '@/hooks/useBustlyDesktopOpen';
import { useTranslation } from '@client-app/i18n';
import {
    AUTH_STORAGE_KEY,
    supabase,
    type OAuthContext,
} from '@common/api/authService';
import bustlyLogo from '@common/assets/icons/logo.svg';
import { SignOut, ArrowSquareOut } from '@phosphor-icons/react';
import React, { useState } from 'react';
import styles from './clientAuth.module.scss';
import Modal from '@/components/Settings/components/Modal';
// @ts-ignore
import Cookies from 'js-cookie';
const clientAuthCoverImg = '/imgs/Cover.jpg';

type ClientAuthProps = {
    mode?: 'static' | 'oauth';
    oauthContext?: OAuthContext | null;
};

const ClientAuth: React.FC<ClientAuthProps> = ({
    mode = 'static',
    oauthContext,
}) => {
    const { t } = useTranslation();
    const { logout, user } = useAuth();
    const [isMismatchModalOpen, setIsMismatchModalOpen] = useState(false);

    const handleSwitchAccount = async () => {
        Cookies.remove(AUTH_STORAGE_KEY);
        localStorage.removeItem(AUTH_STORAGE_KEY);
        try {
            await supabase.auth.signOut({ scope: 'local' });
        } catch (e) {
            console.error(e);
        }
        window.location.href = '/admin/auth';
    };
    const {
        handleOpenBustlyClick,
        isLoading,
        redirectUrl,
        shouldUseContinuationFlow,
    } = useBustlyDesktopOpen({
        enableAutomaticFlow: true,
        oauthReturnContext: oauthContext,
    });

    if (mode !== 'oauth') {
        return (
            <div className={styles.pageContainer}>
                <div className={styles.logoutWrapper}>
                    <button
                        type="button"
                        onClick={logout}
                        className={styles.logoutButton}
                    >
                        <SignOut size={16} weight="bold" />
                        <span>{t('website.clientAuth.logOut')}</span>
                    </button>
                </div>
                
                <div className={styles.headerSection}>
                    <h1 className={styles.title}>
                        {t('website.clientAuth.returnToClient')}
                    </h1>
                    <p className={styles.subtitle}>
                        Already installed? Open Bustly now. If not, download the client first.
                    </p>
                </div>

                <div className={styles.cardsGrid}>
                    <button
                        type="button"
                        onClick={handleOpenBustlyClick}
                        className={styles.card}
                        aria-label={t('website.clientAuth.openBustlyAria')}
                    >
                        <div className={styles.mockupContainer}>
                            <img src={clientAuthCoverImg} alt={t('website.clientAuth.clientMockupAlt')} className={styles.mockupImage} />
                        </div>
                        <span className={styles.primaryButton}>
                            <ArrowSquareOut size={18} weight="bold" />
                            {t('website.clientAuth.openBustly')}
                        </span>
                    </button>
                </div>

                <Modal
                    isOpen={isMismatchModalOpen}
                    onClose={() => setIsMismatchModalOpen(false)}
                    title={t('website.clientAuth.mismatchTitle')}
                    width="max-w-[480px]"
                    bodyClassName="px-6 pb-6 pt-2"
                >
                    <div className="flex flex-col items-center text-center space-y-6 pt-2">
                        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#F4F5F8]">
                            <img src={bustlyLogo} alt="Bustly" className="h-8 w-8 object-contain" />
                        </div>
                        
                        <p className="text-sm leading-6 text-[#666F8D]">
                            {t('website.clientAuth.mismatchDescription', {
                                account: user?.user_metadata?.full_name || user?.email || t('website.userMenu.user'),
                            })}
                        </p>

                        <div className="flex w-full flex-col gap-3">
                            <button
                                onClick={handleSwitchAccount}
                                className="w-full h-11 flex items-center justify-center rounded-xl bg-[#1A162F] text-sm font-medium text-white transition-all hover:bg-[#231D3E] active:scale-[0.99]"
                            >
                                {t('website.clientAuth.switchToClientAccount')}
                            </button>
                            <button
                                onClick={() => setIsMismatchModalOpen(false)}
                                className="w-full h-11 flex items-center justify-center rounded-xl border border-[#E8EBF3] bg-white text-sm font-medium text-[#666F8D] transition-all hover:bg-[#F8F9FC] hover:text-[#1A162F]"
                            >
                                {t('website.clientAuth.continueWithWebsiteAccount')}
                            </button>
                        </div>
                    </div>
                </Modal>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[90] flex flex-col items-center justify-center bg-[#F4F5F8] antialiased">
            <div className="absolute right-6 top-6 z-[92]">
                <button
                    type="button"
                    onClick={logout}
                    className="flex items-center gap-2 rounded-full border border-[#D8DEEB] bg-white/90 px-4 py-2 text-sm font-medium text-[#666F8D] shadow-sm transition-colors hover:text-[#1A162F]"
                >
                    <SignOut size={16} weight="bold" />
                    <span>{t('website.clientAuth.logOut')}</span>
                </button>
            </div>
            <div className="relative z-10 flex flex-col items-center text-center animate-in fade-in zoom-in-95 duration-300">
                <div className="-translate-y-6 mb-[-10px] flex items-center justify-center">
                    <img src={bustlyLogo} alt="Bustly" className="mb-[10px] h-[30px] w-[30px] object-contain" />
                </div>

                <h1 className="mb-3 text-[26px] font-bold tracking-tight text-[#1A162F]">
                    {t('website.clientAuth.successTitle')}
                </h1>

                <p className="mb-10 text-[15px] font-medium text-[#666F8D]">
                    {t('website.clientAuth.redirectPrompt')}
                </p>

                <button
                    type="button"
                    onClick={() => {
                        void handleOpenBustlyClick();
                    }}
                    aria-busy={shouldUseContinuationFlow && isLoading && !redirectUrl ? 'true' : undefined}
                    className="min-w-[240px] rounded-xl bg-[#1A162F] px-16 py-3.5 text-[15px] font-medium text-white shadow-md transition-all hover:bg-[#231D3E] active:scale-95"
                >
                    <span className="flex items-center justify-center gap-2">
                        <ArrowSquareOut size={18} weight="bold" />
                        {t('website.clientAuth.openBustly')}
                    </span>
                </button>
            </div>
        </div>
    );
};

export default ClientAuth;
